/**
 * @file   src\Pages\CWODashboard.tsx
 * @brief  Dashboard page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Row,
  Col,
  Button,
  useEffect,
  useOutletContext,
  useNavigate,
  useState,
  React,
} from "../utils/thirdpartyComponents";
import "../styles/dashboard.scss";
import FilterDropdown from "../components/FilterDropdown";
import Supporticon from "../assets/images/customer-support.svg";
import Usersicon from "../assets/images/users-icon.svg";
import Washlocationicon from "../assets/images/washlocation-icon.svg";
import Hardwareicon from "../assets/images/hardware-icon.svg";
import Washcompleteicon from "../assets/images/washcomplete-icon.svg";
import Revenueicon from "../assets/images/revenue-icon.svg";
import RightarrowIcon from "../assets/images/icons/rightarrow";
import PromotionIcon from "../assets/images/promotion-icon.svg";
import { AppContextType } from "../interfaces/generalInterface";
import { pageURLs } from "../utils/pageUrls";
import { DashBoardApi } from "../utils/apiUrls";
import axios from "../config/axiosConfig";
import SmallLoader from "../components/SmallLoader";
import moment from "moment-timezone";
import { userType } from "../utils/enums";

const CWODashboard = () => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const [loading, setLoading] = useState(false);
  const [filterLabel, setFilterLabel] = useState("Current Month");
  const [dateFilterClass, setDateFilterClass] = useState("");
  const [locationFilterClass, setLocationFilterClass] = useState("");
  const [companyFilterClass, setCompanyFilterClass] = useState("");
  const defaultCarwashDashboard = {
    wash_location_count: 0,
    automatic_wash_location: 0,
    tunnel_wash_location: 0,
    selfBayCount: 0,
    dogBayCount: 0,
    vacuumBayCount: 0,
    app_users: 0,
    subscribed_users: 0,
    active_hardware: 0,
    inactive_hardware: 0,
    current_month_washes: 0,
    average_visit_permonth: 0,
    current_month_revenue: 0,
    last_device_sync_date: "",
    promotionCount: 0,
  };

  const navigate = useNavigate();

  // To display heading
  useEffect(() => {
    handleUpdate();
    const month = new Date().getMonth() + 1;
    const formValues = {
      location: null,
      month: month,
      year: new Date().getFullYear(),
      fromDate: null,
      toDate: null,
      dateFilterType: "month",
      defaultFilter: true,
      dateFilterChanged: false,
      companyFilterChanged: false,
      locationFilterChanged: false,
    };
    getDashboardData(formValues); //commented for proto please dont delete
  }, []);

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Dashboard",
      url: "",
      showBackButton: false,
      showMainMenu: true,
      activeLink: "",
    });
  };

  const [dashboardCount, setDashboardCount] = useState(defaultCarwashDashboard);

  const handleClick = (type: string) => {
    navigate(`/${pageURLs.hardware_details}`, { state: { type: type } });
  };

  const getDashboardData = async (payload: any) => {
    try {
      const timezoneOffset = new Date().getTimezoneOffset();
      let appendQueryString = false;
      let param = `?search=1&offset: ${timezoneOffset}`;

      if (payload.location) {
        appendQueryString = true;
        param += "&locationId=" + payload.location;
      }
      if (payload.month && payload.dateFilterType === "month") {
        setFilterLabel("Current Month");
        appendQueryString = true;
        param += "&month=" + payload.month;
        param += "&year=" + new Date().getFullYear();
      }
      if (payload.year && payload.dateFilterType === "year") {
        setFilterLabel("Current Year");
        appendQueryString = true;
        param += "&year=" + payload.year;
      }
      if (payload.fromDate && payload.dateFilterType === "custom") {
        appendQueryString = true;
        const frdate = moment(payload.fromDate);
        const fromdate = frdate.format("YYYY-MM-DD");
        param += "&fromDate=" + fromdate;
      }
      if (payload.toDate && payload.dateFilterType === "custom") {
        appendQueryString = true;
        const tdate = moment(payload.toDate);
        const todate = tdate.format("YYYY-MM-DD");
        param += "&toDate=" + todate;
      }
      if (payload.dateFilterType === "custom") {
        const locale = "en-us";
        const label =
          new Date(payload.fromDate).toLocaleDateString(locale) +
          " to " +
          new Date(payload.toDate).toLocaleDateString(locale);
        setFilterLabel(label);
      }
      let filterChanged = false;
      if (payload.companyFilterChanged) {
        setLocationFilterClass("filtered");
        setDateFilterClass("filtered");
        filterChanged = true;
      }
      if (payload.locationFilterChanged) {
        setLocationFilterClass("filtered");
        setDateFilterClass("filtered");
        filterChanged = true;
      }
      if (payload.dateFilterChanged) {
        setDateFilterClass("filtered");
        filterChanged = true;
      }
      if (filterChanged === false) {
        setCompanyFilterClass("");
        setLocationFilterClass("");
        setDateFilterClass("");
      }
      setLoading(true);
      const apiUrl = appendQueryString
        ? DashBoardApi.CARWASH_OWNER_DASHBOARD_LIST + param
        : DashBoardApi.CARWASH_OWNER_DASHBOARD_LIST;
      const { data } = await axios.get(apiUrl);
      if (data?.error === 0) {
        setLoading(false);
        const CarwashDashboardData = {
          wash_location_count: data?.data?.locationCount
            ? data?.data?.locationCount
            : 0,
          automatic_wash_location: data?.data?.automaticBayCount
            ? data?.data?.automaticBayCount
            : 0,
          tunnel_wash_location: data?.data?.tunnelBayCount
            ? data?.data?.tunnelBayCount
            : 0,
          selfBayCount: data?.data?.selfBayCount ? data?.data?.selfBayCount : 0,
          dogBayCount: data?.data?.dogBayCount ? data?.data?.dogBayCount : 0,
          vacuumBayCount: data?.data?.vacuumBayCount
            ? data?.data?.vacuumBayCount
            : 0,
          app_users: data?.data?.appUsers ? data?.data?.appUsers : 0,
          subscribed_users: data?.data?.subscribedUsers
            ? data?.data?.subscribedUsers
            : 0,
          active_hardware: data?.data?.activeHardware
            ? data?.data?.activeHardware
            : 0,
          inactive_hardware: data?.data?.inactiveHardware
            ? data?.data?.inactiveHardware
            : 0,
          current_month_washes: data?.data?.washesCompleted
            ? data?.data?.washesCompleted
            : 0,
          average_visit_permonth: data?.data?.averageWashesCompleted
            ? data?.data?.averageWashesCompleted
            : 0,
          current_month_revenue: data?.data?.revenue ? data?.data?.revenue : 0,
          last_device_sync_date: data?.data?.statusUpdateOn
            ? data?.data?.statusUpdateOn
            : "",
          promotionCount: data?.data?.promotionCount
            ? data?.data?.promotionCount
            : 0,
        };
        setDashboardCount(CarwashDashboardData);
      } else {
        setLoading(false);
        setDashboardCount(defaultCarwashDashboard);
      }
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const filterDashBoard = async (payload: any) => {
    getDashboardData(payload);
  };

  return (
    <div className="dashboard-container">
      <Container>
        <div className="filter-top-wrap mb-3 text-end">
          <FilterDropdown
            filterDashBoard={filterDashBoard}
            userRole={userType["CARWASH-OWNER"]}
          />
        </div>
        <div className="db-information">
          <Row>
            <Col lg={7}>
              <div
                className={
                  "dashboard-card location-card " + locationFilterClass
                }
              >
                <h4>WASH LOCATIONS</h4>
                <div className="info-sec d-flex align-items-center">
                  <div className="location-count">
                    <h1 className="f-bold">
                      {dashboardCount.wash_location_count}
                    </h1>
                    <Button
                      variant="secondary"
                      className="d-flex align-items-center secondary-btn btn-details"
                      onClick={() => navigate(`/${pageURLs.carwash_locations}`)}
                    >
                      Details <RightarrowIcon />
                    </Button>
                  </div>
                  <div className="equipment-info location-equipment">
                    <p className="f-semi mb-2">Wash Bay</p>
                    <div className="card-group d-flex">
                      <div className="card-sm automatic">
                        <p className="f-medium">In Bay</p>
                        <h6 className="f-bold">
                          {dashboardCount.automatic_wash_location}
                        </h6>
                      </div>
                      <div className="card-sm tunnel">
                        <p className="f-medium">Tunnel</p>
                        <h6 className="f-bold">
                          {dashboardCount.tunnel_wash_location}
                        </h6>
                      </div>
                      <div className="card-sm tunnel self-serve">
                        <p className="f-medium">Self Serve</p>
                        <h6 className="f-bold">
                          {dashboardCount.selfBayCount}
                        </h6>
                      </div>
                      <div className="card-sm dog-wash">
                        <p className="f-medium">Dog Wash</p>
                        <h6 className="f-bold">{dashboardCount.dogBayCount}</h6>
                      </div>
                      <div className="card-sm vaccum">
                        <p className="f-medium">Vacuum</p>
                        <h6 className="f-bold">
                          {dashboardCount.vacuumBayCount}
                        </h6>
                      </div>
                    </div>
                  </div>
                  {/* <div className="db-icon"><img src={Washlocationicon} alt="Car Wash App" /></div> */}
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div
                className={"dashboard-card users-card " + companyFilterClass}
              >
                <h4>APP USERS</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div className="count">
                    <h1 className="f-bold">{dashboardCount.app_users}</h1>
                  </div>
                  <div className="sub-card">
                    <p className="f-medium">Subscribed Users</p>
                    <h2 className="f-bold">
                      {dashboardCount.subscribed_users}
                    </h2>
                  </div>
                  <div className="db-icon">
                    <img src={Usersicon} alt="Car Wash App" />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <div
                className={
                  "dashboard-card hardware-card position-relative " +
                  locationFilterClass
                }
              >
                <h4>HARDWARE</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div
                    className="activity-card active"
                    onClick={() => handleClick("active")}
                  >
                    <p className="f-medium">Active</p>
                    <div className="count d-flex align-items-center">
                      <h2 className="f-bold">
                        {dashboardCount.active_hardware}
                      </h2>
                    </div>
                    <Button
                      variant="secondary"
                      className="d-flex align-items-center secondary-btn btn-details"
                    >
                      Details <RightarrowIcon />
                    </Button>
                  </div>
                  <div
                    className="activity-card inactive"
                    onClick={() => handleClick("inactive")}
                  >
                    <p className="f-medium">Inactive</p>
                    <div className="count d-flex align-items-center">
                      <h2 className="f-bold">
                        {dashboardCount.inactive_hardware}
                      </h2>
                    </div>
                    <Button
                      variant="secondary"
                      className="d-flex align-items-center secondary-btn btn-details"
                    >
                      Details <RightarrowIcon />
                    </Button>
                  </div>
                  <div className="db-icon">
                    <img src={Hardwareicon} alt="Car Wash App" />
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div
                className={"dashboard-card completed-card " + dateFilterClass}
              >
                <h4>washes completed</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div className="count d-flex flex-column">
                    <div className="month-label f-medium">{filterLabel}</div>
                    <h1 className="f-bold">
                      {dashboardCount.current_month_washes}
                    </h1>
                  </div>
                  <div className="sub-card">
                    <p>Average Visits /30 Days</p>
                    <h2 className="f-bold">
                      {dashboardCount.average_visit_permonth}
                    </h2>
                  </div>
                  <div className="db-icon">
                    <img src={Washcompleteicon} alt="Car Wash App" />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={4}>
              <div
                className={
                  "dashboard-card revenue-card position-relative " +
                  dateFilterClass
                }
              >
                <h4>REVENUE</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div className="revenue-info">
                    <div className="month-label f-medium">{filterLabel}</div>
                    <div className="count d-flex align-items-center">
                      <h1 className="f-bold">
                        $ {dashboardCount.current_month_revenue}
                      </h1>
                    </div>
                  </div>
                  <div className="db-icon">
                    <img src={Revenueicon} alt="Car Wash App" />
                  </div>
                </div>
                <Button
                  variant="secondary"
                  className="d-flex align-items-center secondary-btn btn-details"
                  onClick={() => navigate(`/${pageURLs.revenue_reports}`)}
                >
                  Details <RightarrowIcon />
                </Button>
              </div>
            </Col>
            <Col lg={4}>
              <div
                className="dashboard-card revenue-card position-relative"
                onClick={() => navigate(`/${pageURLs.manage_promotions}`)}
              >
                <h4>Promotions</h4>
                <div className="info-sec d-flex justify-content-between align-items-center">
                  <div className="revenue-info">
                    <div className="month-label active-label f-medium">
                      Active
                    </div>
                    <div className="count d-flex align-items-center">
                      <h1 className="f-bold">
                        {dashboardCount.promotionCount}
                      </h1>
                    </div>
                  </div>
                  <div className="db-icon">
                    <img src={PromotionIcon} alt="Car Wash App" />
                  </div>
                </div>
                <Button
                  variant="secondary"
                  className="d-flex align-items-center secondary-btn btn-details"
                >
                  Details <RightarrowIcon />
                </Button>
              </div>
            </Col>

            <Col lg={4}>
              <div
                className="customer-support-desk d-flex align-items-center justify-content-between"
                onClick={() => navigate(`/${pageURLs.customer_support_desk}`)}
              >
                <h5>
                  Customer <br />
                  <span className="f-bold">Support</span> Desk
                </h5>
                <img src={Supporticon} alt="Car Wash App" />
              </div>
            </Col>
          </Row>
        </div>
      </Container>
      {loading && <SmallLoader />}
    </div>
  );
};

export default CWODashboard;
